// packages/web/src/stores/homeworkStore.ts
import { create } from 'zustand';
import { homeworkApi, Homework, HomeworkDetail, StudentProgress } from '../services/homeworkApi';

interface CreateHomeworkForm {
  name: string;
  description: string;
  dueDate: string;
  selectedExerciseIds: string[];
  assignments: Array<{ type: 'student' | 'class'; id: string }>;
}

interface HomeworkState {
  homeworks: Homework[];
  currentHomework: HomeworkDetail | null;
  homeworkProgress: {
    homework_id: string;
    total_students: number;
    completed_students: number;
    completion_rate: number;
    student_progress: StudentProgress[];
  } | null;
  loading: boolean;
  error: string | null;
  
  // 创建作业表单
  createForm: CreateHomeworkForm;
  
  // Actions
  fetchHomeworks: (filters?: any) => Promise<void>;
  fetchHomeworkDetail: (id: string) => Promise<void>;
  fetchHomeworkProgress: (id: string) => Promise<void>;
  createHomework: () => Promise<void>;
  updateHomework: (id: string, updates: any) => Promise<void>;
  deleteHomework: (id: string) => Promise<void>;
  addExercisesToHomework: (homeworkId: string, exerciseIds: string[]) => Promise<void>;
  
  // Form actions
  setCreateFormField: (field: keyof CreateHomeworkForm, value: any) => void;
  resetCreateForm: () => void;
}

const initialCreateForm: CreateHomeworkForm = {
  name: '',
  description: '',
  dueDate: '',
  selectedExerciseIds: [],
  assignments: [],
};

export const useHomeworkStore = create<HomeworkState>((set, get) => ({
  homeworks: [],
  currentHomework: null,
  homeworkProgress: null,
  loading: false,
  error: null,
  createForm: initialCreateForm,
  
  fetchHomeworks: async (filters) => {
    set({ loading: true, error: null });
    try {
      const response = await homeworkApi.getList(filters);
      set({ homeworks: response.data.homeworks, loading: false });
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Failed to fetch homeworks',
        loading: false 
      });
    }
  },
  
  fetchHomeworkDetail: async (id) => {
    set({ loading: true, error: null });
    try {
      const response = await homeworkApi.getDetail(id);
      set({ currentHomework: response.data, loading: false });
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Failed to fetch homework detail',
        loading: false 
      });
    }
  },
  
  fetchHomeworkProgress: async (id) => {
    set({ loading: true, error: null });
    try {
      const response = await homeworkApi.getProgress(id);
      set({ homeworkProgress: response.data, loading: false });
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Failed to fetch homework progress',
        loading: false 
      });
    }
  },
  
  createHomework: async () => {
    set({ loading: true, error: null });
    try {
      const { createForm } = get();
      await homeworkApi.create({
        name: createForm.name,
        description: createForm.description,
        dueDate: createForm.dueDate,
        exerciseIds: createForm.selectedExerciseIds,
        assignments: createForm.assignments,
      });
      
      set({ loading: false });
      get().resetCreateForm();
      await get().fetchHomeworks();
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Failed to create homework',
        loading: false 
      });
      throw error;
    }
  },
  
  updateHomework: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      await homeworkApi.update(id, updates);
      set({ loading: false });
      await get().fetchHomeworks();
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Failed to update homework',
        loading: false 
      });
      throw error;
    }
  },
  
  deleteHomework: async (id) => {
    set({ loading: true, error: null });
    try {
      await homeworkApi.delete(id);
      set({ loading: false });
      await get().fetchHomeworks();
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Failed to delete homework',
        loading: false 
      });
      throw error;
    }
  },
  
  addExercisesToHomework: async (homeworkId, exerciseIds) => {
    set({ loading: true, error: null });
    try {
      await homeworkApi.addExercises(homeworkId, exerciseIds);
      set({ loading: false });
      await get().fetchHomeworkDetail(homeworkId);
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Failed to add exercises',
        loading: false 
      });
      throw error;
    }
  },
  
  setCreateFormField: (field, value) => set((state) => ({
    createForm: { ...state.createForm, [field]: value },
  })),
  
  resetCreateForm: () => set({ createForm: initialCreateForm }),
}));
